<template>
  <section class="mx-4 h-full" id="priceCollectionDocumentsManagement">
    <BaseTable
      :headers="tableHeaders"
      :list="reportingTemplates"
      table-id="reporting-templates-view"
      clickable
      @refresh-data="refreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @table-item-clicked="onItemClick">
      <template #filters>
        <section class="grid grid-cols-2 mx-auto mt-5 mb-20 w-full max-w-xl">
          <div class="justify-self-start space-y-4">
            <BaseToggle label="Manager" name="isManager" v-model="filterIsManager" />
            <BaseToggle label="Client" name="isFromManager" v-model="filterIsClient" />
            <div>
              <BaseTriStateToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                v-model="filterIsActive" />
            </div>
          </div>
          <div class="justify-self-center space-y-4 w-full">
            <BaseMultiSelectDropdown
              v-model="filterFunctionality"
              class="w-full h-full text-sm"
              tags-outside
              place-holder-text="Select category"
              :list-items="dropdownFunctionality"></BaseMultiSelectDropdown>
          </div>
        </section>
      </template>
      <template #actions="{ item }">
        <div class="flex gap-1 justify-center items-center">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item as ReportingTemplate)" />
        </div>
      </template>
    </BaseTable>

    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text> {{ $t('messages.info.deleteCascade') }}</template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  getReportingTemplatesByCondition,
  removeReportingTemplatesByIds,
} from '@/services/api/reporting';
import { useI18n } from 'vue-i18n';
import { enumToDropdownItems } from '@/utils/enums';
import { v4 as uuidv4 } from 'uuid';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseMultiSelectDropdown from '@/components/common/BaseMultiSelectDropdown.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import type { SelectDropdownItem, TableFilterData } from '@/types/Base';
import type { ReportingTemplate } from '@/types/Reporting';
import { eDivision } from '@/types/Division';
import { useToastStore } from '@/stores/toast';
import { useRouter } from 'vue-router';

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  {
    value: 'name',
    text: $t('baseObject.name'),
    type: 'text',
    active: true,
  },
  {
    value: 'code',
    text: $t('baseObject.code'),
    type: 'text',
    active: true,
  },
  {
    value: 'documentType.name',
    text: $t('reporting.documentType'),
    type: 'text',
    active: true,
  },
  { value: 'isManager', text: 'Manager', type: 'check', active: true },
  { value: 'isClient', text: 'Client', type: 'check', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const reportingTemplates = ref<ReportingTemplate[]>([]);
const reportingTemplatesCount = ref<number>();
const filterIsManager = ref(false);
const filterIsClient = ref(false);
const dropdownFunctionality = ref(enumToDropdownItems(eDivision));
const filterFunctionality = ref<SelectDropdownItem[]>([]);
const filterIsActive = ref<boolean | undefined>(undefined);

onMounted(async () => {
  await loadData({
    itemsPerPage: 50,
    sortAscending: true,
    page: 1,
  });
});

async function loadData(
  params: TableFilterData & {
    functionalityEnums?: number[];
    isManager?: boolean;
    isClient?: boolean;
  }
) {
  const res = await getReportingTemplatesByCondition(params);
  if (res) {
    reportingTemplates.value = res.data;
    reportingTemplatesCount.value = res.count;
  }
}

async function refreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? tableHeaders[data.column]?.value : 'id';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);
}

async function onApplyFilters(
  data: TableFilterData & { functionalityEnums?: number[]; isManager?: boolean; isClient?: boolean }
) {
  data.functionalityEnums = filterFunctionality.value.map((x) => Number(x.value));
  data.isManager = filterIsManager.value;
  data.isClient = filterIsClient.value;
  data.isActive = filterIsActive.value;
  await loadData(data);
}

async function onResetFilters() {
  filterFunctionality.value = [];
  filterIsManager.value = false;
  filterIsClient.value = false;
  filterIsActive.value = undefined;
  await loadData({
    itemsPerPage: 50,
    sortColumn: 'createdDate',
    sortAscending: true,
    page: 1,
  });
}

const confirmDeleteSingleModalActive = ref(false);
const templateToDeleteSingle = ref<ReportingTemplate>();

function deleteSingleConfirm(item: ReportingTemplate) {
  templateToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

const toastStore = useToastStore();

async function onDeleteMultiselect(items: ReportingTemplate[]) {
  const idsToDelete = items.filter((x) => x.isFromManager).map((item) => item.id);
  if (idsToDelete.length === 0) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }
  const res = await removeReportingTemplatesByIds(idsToDelete);
  if (!res && reportingTemplates.value)
    reportingTemplates.value = reportingTemplates.value.filter((x) => !idsToDelete.includes(x.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  if (!templateToDeleteSingle.value) return;
  const idToDelete = templateToDeleteSingle.value.id;
  const res = await removeReportingTemplatesByIds([idToDelete]);
  if (!res && reportingTemplates.value)
    reportingTemplates.value = reportingTemplates.value.filter((x) => x.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  confirmDeleteSingleModalActive.value = false;
}

const router = useRouter();
function onAddNew() {
  router.push({ name: 'reporting-designer', params: { id: uuidv4() } });
}

function onItemClick(item: ReportingTemplate) {
  router.push({ name: 'reporting-designer', params: { id: item.id } });
}
</script>
